import { getGlobalLogger } from '@zg-rentals/logger-base';

const logger = getGlobalLogger('googleSignInUtils');

export interface GoogleSignInConfig {
  clientId: string;
  onCredentialResponse: (response: GoogleCredentialResponse) => void;
  onError?: (error: string) => void;
  onSuccess?: () => void;
  autoPrompt?: boolean;
  buttonConfig?: GoogleButtonConfiguration;
}

// Type aliases for Google Sign In types
type GoogleCredentialResponse = {
  credential: string;
  select_by?: string;
  client_id?: string;
};

type GoogleButtonConfiguration = {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: string | number;
  locale?: string;
  click_listener?: () => void;
};

type GooglePromptMomentNotification = {
  isDisplayMoment(): boolean;
  isDisplayed(): boolean;
  isNotDisplayed(): boolean;
  getNotDisplayedReason(): string;
  isSkippedMoment(): boolean;
  getSkippedReason(): string;
  isDismissedMoment(): boolean;
  getDismissedReason(): string;
  getMomentType(): string;
};

export interface GoogleSignInInstance {
  renderButton: (containerId: string) => boolean;
  prompt: () => void;
  isInitialized: () => boolean;
  destroy: () => void;
}

class GoogleSignIn implements GoogleSignInInstance {
  private config: GoogleSignInConfig;
  private initialized = false;
  private buttonContainerId?: string;
  private observer?: MutationObserver;

  constructor(config: GoogleSignInConfig) {
    this.config = config;
  }

  initialize(): void {
    if (!window.google?.accounts?.id) {
      logger?.error('Google Sign In API not loaded');
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: this.config.clientId,
        callback: this.config.onCredentialResponse,
      });

      this.initialized = true;

      if (this.config.autoPrompt !== false) {
        this.prompt();
      }

      if (this.config.onSuccess) {
        this.config.onSuccess();
      }
    } catch (error) {
      logger?.error('Failed to initialize Google Sign In:', error);
      if (this.config.onError) {
        this.config.onError('Failed to initialize Google Sign In');
      }
    }
  }

  renderButton(containerId: string): boolean {
    if (!this.initialized) {
      logger?.error('Google Sign In not initialized');
      return false;
    }

    const container = document.getElementById(containerId);
    if (!container) {
      logger?.error(`Container with id "${containerId}" not found`);
      return false;
    }

    try {
      this.buttonContainerId = containerId;

      // Hide any existing fallback button
      const existingButton = container.querySelector('.PromptEmail-siwg-default-button') as HTMLElement;
      if (existingButton) {
        existingButton.style.display = 'none';
      }

      // Render the Google button
      window.google.accounts!.id.renderButton(
        container,
        this.config.buttonConfig || {
          size: 'large',
          text: 'signin_with',
          theme: 'filled_blue',
          width: '224',
        },
      );

      // Set up observer to detect when iframe is rendered
      this.observeButtonRendering(container);

      logger?.info('Google Sign In button render initiated');
      return true;
    } catch (error) {
      logger?.error('Failed to render Google Sign In button:', error);
      return false;
    }
  }

  private observeButtonRendering(container: HTMLElement): void {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node: Node) => {
          if (node.nodeName === 'IFRAME' || (node instanceof Element && node.querySelector('iframe'))) {
            logger?.info('Google button iframe rendered successfully');
            window.dispatchEvent(new Event('google-button-rendered'));
          }
        });
      });
    });

    this.observer.observe(container, {
      childList: true,
      subtree: true,
    });

    // Also check after a short delay
    setTimeout(() => {
      const iframe = container.querySelector('iframe');
      if (iframe) {
        logger?.info('Google button iframe confirmed');
        window.dispatchEvent(new Event('google-button-rendered'));
      }
    }, 100);
  }

  prompt(): void {
    if (!this.initialized) {
      logger?.error('Google Sign In not initialized');
      return;
    }

    try {
      window.google.accounts!.id.prompt((notification: GooglePromptMomentNotification) => {
        if (notification.isNotDisplayed()) {
          logger?.info('Google One Tap not displayed, reason:', notification.getNotDisplayedReason());
        } else {
          logger?.info('Google One Tap displayed');
        }
      });
    } catch (error) {
      logger?.error('Failed to show Google One Tap prompt:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }

    if (window.google?.accounts?.id) {
      window.google.accounts!.id.cancel();
    }

    this.initialized = false;
  }
}

let instance: GoogleSignIn | null = null;

export const createGoogleSignIn = (config: GoogleSignInConfig): GoogleSignInInstance => {
  if (instance) {
    instance.destroy();
  }

  instance = new GoogleSignIn(config);
  instance.initialize();

  return instance;
};

export const getGoogleSignIn = (): GoogleSignInInstance | null => {
  return instance;
};

export const waitForGoogleApi = (): Promise<void> => {
  return new Promise((resolve) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }

    const checkInterval = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(checkInterval);
        resolve();
      }
    }, 100);

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      resolve();
    }, 10000);
  });
};
